// Reference Shelf — a poster wall of the P0 "dual-coding" infographics. Each
// poster is a self-contained HTML-string builder in the same visual language as
// the verb-group boot diagrams (gradient cards, mono grid cells, red = the thing
// that changes / the trap, sage = the regular/default). Every poster links to its
// full handbook article so the gallery is both a memory aid and a table of
// contents. View signature mirrors the other views: (mountEl, params).
import { navigate } from "../router/router.js";

// ---- Poster 1: Essere vs Stare decision tree ----------------------------------
function serEstarPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Which "to be"? Follow the branch</p>
      <div class="flex flex-col items-center gap-2 text-sm">
        <div class="bg-white border-2 border-sage-300 rounded-lg px-4 py-2 font-bold text-sage-800">ESSERE o STARE?</div>
        <div class="text-sage-300 text-lg leading-none">▾</div>
        <div class="grid sm:grid-cols-2 gap-3 w-full">
          <div class="bg-white border border-sage-200 rounded-lg p-3">
            <p class="text-center font-bold text-sage-800 mb-1">ESSERE</p>
            <p class="text-center text-xs text-sage-500 mb-2">essence — who/what it <i>is</i> (and most descriptions)</p>
            <div class="font-mono text-xs grid grid-cols-1 gap-0.5 text-sage-700">
              <div><b class="text-sage-900">D</b>escription — È alto.</div>
              <div><b class="text-sage-900">O</b>ccupation — Sono medico.</div>
              <div><b class="text-sage-900">C</b>haracteristic — È gentile.</div>
              <div><b class="text-sage-900">T</b>ime / date — Sono le due.</div>
              <div><b class="text-sage-900">O</b>rigin — È di Roma.</div>
              <div><b class="text-sage-900">R</b>elationship — È mia madre.</div>
            </div>
            <p class="text-center text-xs text-sage-400 mt-2 italic">mnemonic: <b>DOCTOR</b></p>
          </div>
          <div class="bg-red-50 border border-red-200 rounded-lg p-3">
            <p class="text-center font-bold text-red-700 mb-1">STARE</p>
            <p class="text-center text-xs text-sage-500 mb-2">how you are &amp; where — a narrow set of uses</p>
            <div class="font-mono text-xs grid grid-cols-1 gap-0.5 text-sage-700">
              <div><b class="text-red-700">H</b>ealth — Sto bene.</div>
              <div><b class="text-red-700">L</b>ocation / stay — Sto a casa.</div>
              <div><b class="text-red-700">P</b>rogressive (-ndo) — Sto leggendo.</div>
              <div><b class="text-red-700">S</b>et phrase — Stai attento!</div>
              <div><b class="text-red-700">A</b>bout to (stare per) — Sto per uscire.</div>
            </div>
            <p class="text-center text-xs text-sage-400 mt-2 italic">stare = feel · stay · -ing</p>
          </div>
        </div>
        <p class="text-xs text-sage-500 mt-1 italic text-center max-w-md">Default to <b>essere</b> for descriptions and feelings (sono stanco). Reach for <b>stare</b> for health, location, the -ing progressive and set phrases.</p>
      </div>
    </div>`;
}

// ---- Poster 2: Di vs Da map ---------------------------------------------------
function porParaPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Two short prepositions English keeps confusing</p>
      <div class="grid sm:grid-cols-2 gap-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="text-center font-bold text-sage-800 mb-2">DI <span class="font-normal text-xs text-sage-400">— of / belonging</span></p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div><b class="text-sage-900">possession</b><br>il libro di Marco.</div>
            <div><b class="text-sage-900">material</b><br>un tavolo di legno.</div>
            <div><b class="text-sage-900">topic</b><br>parliamo di musica.</div>
            <div><b class="text-sage-900">origin (where from)</b><br>Sono di Roma.</div>
            <div><b class="text-sage-900">comparison</b><br>più alto di me.</div>
          </div>
        </div>
        <div class="bg-red-50 border border-red-200 rounded-lg p-3">
          <p class="text-center font-bold text-red-700 mb-2">DA <span class="font-normal text-xs text-sage-400">— from / by / at</span></p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div><b class="text-red-700">motion from</b><br>Torno da Roma.</div>
            <div><b class="text-red-700">agent ("by")</b><br>scritto da Dante.</div>
            <div><b class="text-red-700">at someone's place</b><br>Vado dal medico.</div>
            <div><b class="text-red-700">purpose / use</b><br>qualcosa da bere.</div>
            <div><b class="text-red-700">since (a point in time)</b><br>Vivo qui da maggio.</div>
          </div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">Quick test: <b>di</b> answers "whose / of what?"; <b>da</b> answers "from where / by whom?".</p>
    </div>`;
}

// ---- Poster 3: Gender-ending cheat sheet --------------------------------------
function genderPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Predict a noun's gender from its ending</p>
      <div class="grid sm:grid-cols-2 gap-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="text-center font-bold text-sage-800 mb-2">usually <span class="text-sage-500">il</span> (masculine)</p>
          <div class="flex flex-wrap gap-1.5 justify-center font-mono text-xs">
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-o · libro</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-ore · colore</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-ma · problema</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-one · pallone</span>
          </div>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="text-center font-bold text-sage-800 mb-2">usually <span class="text-sage-500">la</span> (feminine)</p>
          <div class="flex flex-wrap gap-1.5 justify-center font-mono text-xs">
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-a · casa</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-zione · stazione</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-sione · decisione</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-tà · città</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-tù · virtù</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-i · crisi</span>
          </div>
        </div>
      </div>
      <div class="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
        <p class="text-center font-bold text-red-700 mb-2">⚠ the traps — learn these by heart</p>
        <div class="flex flex-wrap gap-1.5 justify-center font-mono text-xs">
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">la mano</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">la foto</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">la radio</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">il poeta</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">il problema</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">il cinema</span>
        </div>
        <p class="text-center text-xs text-sage-500 mt-2 italic">Greek-origin <b>-ma</b> words are masculine; <i>mano</i>, <i>foto</i> &amp; <i>radio</i> end in -o but are feminine. Nouns in <b>-e</b> can go either way.</p>
      </div>
    </div>`;
}

// ---- Poster 4: Sound–spelling chart -------------------------------------------
function soundsPoster() {
  return `
    <div class="bg-gradient-to-br from-sage-50 to-white border border-sage-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Letter → sound (the ones that trip up English speakers)</p>
      <p class="text-xs text-sage-500 mb-1.5">The five vowels — pure &amp; constant</p>
      <div class="grid grid-cols-5 gap-1.5 text-center font-mono text-sm mb-3">
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">a</b><br><span class="text-xs text-sage-500">ah</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">e</b><br><span class="text-xs text-sage-500">eh</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">i</b><br><span class="text-xs text-sage-500">ee</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">o</b><br><span class="text-xs text-sage-500">oh</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">u</b><br><span class="text-xs text-sage-500">oo</span></div>
      </div>
      <p class="text-xs text-sage-500 mb-1.5">The tricky consonants &amp; digraphs</p>
      <div class="grid sm:grid-cols-2 gap-x-4 gap-y-1 font-mono text-xs text-sage-700">
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">c</b> + e/i</span><span>soft <i>ch</i> — cena</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b>c</b> + a/o/u</span><span>hard <i>k</i> — casa</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">g</b> + e/i</span><span>soft <i>j</i> — gelato</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b>g</b> + a/o/u</span><span>hard <i>g</i> — gatto</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">ch</b> / <b class="text-red-700">gh</b></span><span>hard <i>k</i> / <i>g</i> — chi, ghiro</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">h</b></span><span><b>silent</b> — ho</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">gn</b></span><span><i>ny</i> — gnocchi</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">gli</b></span><span><i>lli</i> — figlio</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">sc</b> + e/i</span><span><i>sh</i> — pesce</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">z</b></span><span><i>ts / dz</i> — zio</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">s</b> (vowels)</span><span><i>z</i> — rosa</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b>double</b> cons.</span><span>held long — nonno</span></div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic">Red letters are the ones that differ most from English. Master those and your accent jumps.</p>
    </div>`;
}

// ---- Poster 5: Accent-rules flowchart -----------------------------------------
function accentPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Does the word need a written accent?</p>
      <div class="flex flex-col gap-2 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 mb-1">Step 1 — stress is mostly unwritten</p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div>most words stress the <b>2nd-to-last</b> syllable · ca<b class="text-sage-900">sa</b>, a<b class="text-sage-900">mi</b>co</div>
            <div>some stress <b>further back</b>, still unmarked · <b class="text-sage-900">ta</b>volo, <b class="text-sage-900">mu</b>sica</div>
          </div>
        </div>
        <div class="text-sage-300 text-lg leading-none text-center">▾</div>
        <div class="bg-red-50 border border-red-200 rounded-lg p-3">
          <p class="font-bold text-red-700 mb-1">Step 2 — write the accent only on a final-stressed vowel</p>
          <div class="font-mono text-xs grid sm:grid-cols-2 gap-x-4 gap-y-0.5 text-sage-700">
            <div>caf<b class="text-red-700">fè</b> · cit<b class="text-red-700">tà</b></div>
            <div>per<b class="text-red-700">ché</b> · pe<b class="text-red-700">rò</b></div>
            <div>vir<b class="text-red-700">tù</b> · lune<b class="text-red-700">dì</b></div>
            <div>par<b class="text-red-700">lò</b> · cio<b class="text-red-700">è</b></div>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-3 gap-2 mt-3 text-center text-xs font-mono">
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-800">piane</b><br>stress 2nd-last<br><span class="text-sage-400">casa, amico</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-800">sdrucciole</b><br>stress 3rd-last<br><span class="text-sage-400">no mark · tavolo</span></div>
        <div class="bg-red-50 border border-red-200 rounded-lg p-2"><b class="text-red-700">tronche</b><br>stress last<br><span class="text-sage-400">always written! caffè</span></div>
      </div>
    </div>`;
}

// ---- Poster 6: Piacere construction diagram -----------------------------------
function gustarPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">"Mi piace" = <i>it pleases me</i>, not "I like"</p>
      <div class="bg-white border border-sage-200 rounded-lg p-4">
        <div class="flex flex-wrap items-end justify-center gap-2 font-mono text-sm">
          <div class="text-center">
            <div class="bg-sage-50 border border-sage-200 text-sage-500 px-3 py-1.5 rounded-lg">(A me)</div>
            <p class="text-xs text-sage-400 mt-1">optional</p>
          </div>
          <div class="text-center">
            <div class="bg-red-100 border-2 border-red-300 text-red-700 px-3 py-1.5 rounded-lg font-bold">mi</div>
            <p class="text-xs text-sage-400 mt-1">who's pleased</p>
          </div>
          <div class="text-center">
            <div class="bg-red-100 border-2 border-red-300 text-red-700 px-3 py-1.5 rounded-lg font-bold">piace / piacciono</div>
            <p class="text-xs text-sage-400 mt-1">agrees with →</p>
          </div>
          <div class="text-center">
            <div class="bg-sage-50 border border-sage-200 text-sage-700 px-3 py-1.5 rounded-lg">il caffè / i libri</div>
            <p class="text-xs text-sage-400 mt-1">the real subject</p>
          </div>
        </div>
      </div>
      <div class="grid sm:grid-cols-2 gap-3 mt-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 text-xs mb-1.5">who is pleased (indirect object)</p>
          <div class="font-mono text-xs grid grid-cols-2 gap-x-3 gap-y-0.5 text-sage-700">
            <div>mi — I</div><div>ci — we</div>
            <div>ti — you</div><div>vi — you all</div>
            <div>gli / le — he/she</div><div>gli — they</div>
          </div>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 text-xs mb-1.5">pick the verb form by the <i>thing</i></p>
          <div class="font-mono text-xs space-y-0.5 text-sage-700">
            <div>one thing → <b class="text-red-700">piace</b> · Mi piace il caffè.</div>
            <div>many → <b class="text-red-700">piacciono</b> · Mi piacciono i libri.</div>
            <div>an action → <b class="text-red-700">piace</b> · Mi piace ballare.</div>
          </div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">Read it backwards: <i>Mi piacciono i libri</i> → "the books please me" → "I like the books."</p>
    </div>`;
}

// ---- Poster 7: Passato Prossimo vs Imperfetto (photo vs film) -----------------
function preteritoImperfectoPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Two past tenses — pick a <i>viewpoint</i>, not a time</p>
      <div class="grid sm:grid-cols-2 gap-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="text-center font-bold text-sage-800 mb-1">📷 PASSATO PROSSIMO</p>
          <p class="text-center text-xs text-sage-500 mb-2">a photo — one bounded, finished event</p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div><b class="text-sage-900">single done action</b><br>Ho mangiato alle due.</div>
            <div><b class="text-sage-900">sequence of events</b><br>È arrivato, ha mangiato ed è uscito.</div>
            <div><b class="text-sage-900">the interruption</b><br>…quando è squillato il telefono.</div>
          </div>
          <div class="flex flex-wrap gap-1 justify-center mt-2 font-mono text-[11px]">
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-1.5 py-0.5 rounded">ieri</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-1.5 py-0.5 rounded">all'improvviso</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-1.5 py-0.5 rounded">ieri sera</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-1.5 py-0.5 rounded">una volta</span>
          </div>
        </div>
        <div class="bg-red-50 border border-red-200 rounded-lg p-3">
          <p class="text-center font-bold text-red-700 mb-1">🎬 IMPERFETTO</p>
          <p class="text-center text-xs text-sage-500 mb-2">a film rolling — ongoing, habitual, scenery</p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div><b class="text-red-700">background / scene</b><br>Era notte e pioveva.</div>
            <div><b class="text-red-700">habit / routine</b><br>Andavo al mare ogni estate.</div>
            <div><b class="text-red-700">the thing interrupted</b><br>Leggevo quando…</div>
          </div>
          <div class="flex flex-wrap gap-1 justify-center mt-2 font-mono text-[11px]">
            <span class="bg-red-100 border border-red-300 text-red-700 px-1.5 py-0.5 rounded">sempre</span>
            <span class="bg-red-100 border border-red-300 text-red-700 px-1.5 py-0.5 rounded">spesso</span>
            <span class="bg-red-100 border border-red-300 text-red-700 px-1.5 py-0.5 rounded">ogni giorno</span>
            <span class="bg-red-100 border border-red-300 text-red-700 px-1.5 py-0.5 rounded">mentre</span>
          </div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">Together: the <b>imperfetto</b> paints the scene; the <b>passato prossimo</b> drops in the event that moves the story.</p>
    </div>`;
}

// ---- Poster 8: RID object-pronoun order ---------------------------------------
function pronounOrderPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Stacking pronouns? They line up in <b>RID</b> order</p>
      <div class="bg-white border border-sage-200 rounded-lg p-4">
        <div class="flex flex-wrap items-end justify-center gap-2 font-mono text-sm">
          <div class="text-center">
            <div class="bg-sage-50 border border-sage-200 text-sage-700 px-3 py-1.5 rounded-lg font-bold">R</div>
            <p class="text-xs text-sage-400 mt-1">reflexive<br>mi ti si ci vi si</p>
          </div>
          <div class="text-sage-300 text-lg leading-none pb-3">+</div>
          <div class="text-center">
            <div class="bg-red-100 border-2 border-red-300 text-red-700 px-3 py-1.5 rounded-lg font-bold">I</div>
            <p class="text-xs text-sage-400 mt-1">indirect<br>mi ti gli le ci vi</p>
          </div>
          <div class="text-sage-300 text-lg leading-none pb-3">+</div>
          <div class="text-center">
            <div class="bg-sage-50 border border-sage-200 text-sage-700 px-3 py-1.5 rounded-lg font-bold">D</div>
            <p class="text-xs text-sage-400 mt-1">direct<br>lo la li le</p>
          </div>
          <div class="text-sage-300 text-lg leading-none pb-3">+</div>
          <div class="text-center">
            <div class="bg-white border border-sage-300 text-sage-800 px-3 py-1.5 rounded-lg font-bold">verb</div>
          </div>
        </div>
      </div>
      <div class="grid sm:grid-cols-2 gap-3 mt-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 text-xs mb-1.5">the glie- merge</p>
          <div class="font-mono text-xs space-y-0.5 text-sage-700">
            <div class="text-red-700">✗ gli lo do</div>
            <div class="text-sage-900">✓ <b>glielo</b> do</div>
            <div class="text-sage-400 mt-1 not-italic">gli/le + lo/la/ne → one word: <b>glielo, gliela, gliene</b></div>
          </div>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 text-xs mb-1.5">where they sit</p>
          <div class="font-mono text-xs space-y-0.5 text-sage-700">
            <div><b class="text-sage-900">before</b> a conjugated verb · Me lo dà.</div>
            <div><b class="text-red-700">attached</b> to infinitive · darmelo</div>
            <div><b class="text-red-700">attached</b> to a command · Dammelo!</div>
          </div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">Indirect comes first and shifts vowel: <b>me lo</b>, <b>te la</b>, <b>glieli</b> — mi/ti/ci/vi become me/te/ce/ve.</p>
    </div>`;
}

// ---- Poster 9: WEIRDO subjunctive triggers ------------------------------------
function weirdoPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">What flips the verb into the subjunctive: <b>WEIRDO</b></p>
      <div class="grid sm:grid-cols-2 gap-2 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">W</b>ishes — volere / sperare che<br><span class="text-sage-500">Voglio che tu studi.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">E</b>motions — essere felice / temere che<br><span class="text-sage-500">Sono felice che tu venga.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">I</b>mpersonal — è necessario che<br><span class="text-sage-500">È importante che tu lo sappia.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">R</b>ecommendations — chiedere / consigliare che<br><span class="text-sage-500">Chiedo che tu aspetti.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">D</b>oubt / Denial — dubitare / non credere che<br><span class="text-sage-500">Dubito che sia vero.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">O</b>ptative — magari<br><span class="text-sage-500">Magari piovesse presto!</span>
        </div>
      </div>
      <div class="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
        <p class="text-center font-bold text-red-700 text-xs mb-1.5">two subjects + <b>che</b> are the gate</p>
        <div class="font-mono text-xs text-center text-sage-700 space-y-0.5">
          <div>same subject → infinitive · Voglio <b class="text-sage-900">andare</b>.</div>
          <div>different subject → subjunctive · Voglio che <b class="text-red-700">tu vada</b>.</div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">Build it: from the <b>io</b> present, -are → -i, -ere/-ire → -a (parlo→parli, prendo→prenda). All three singular persons match.</p>
    </div>`;
}

// Poster registry — id, title, subtitle, builder, and the article slug it links to.
const POSTERS = [
  { id: "ser-estar", title: "Essere vs Stare", subtitle: "Which \"to be\" to use", build: serEstarPoster, slug: "essere-vs-stare" },
  { id: "por-para", title: "Di vs Da", subtitle: "The two prepositions by function", build: porParaPoster, slug: "di-vs-da" },
  { id: "gender", title: "Noun Gender Endings", subtitle: "Predict il/la + the traps", build: genderPoster, slug: "gender-of-nouns" },
  { id: "sounds", title: "Sound–Spelling Chart", subtitle: "Letter → sound", build: soundsPoster, slug: "pronunciation-and-the-alphabet" },
  { id: "accents", title: "Accent Rules", subtitle: "Where the written accent goes", build: accentPoster, slug: "stress-and-accents" },
  { id: "gustar", title: "Piacere Construction", subtitle: "The \"mi piace\" inversion", build: gustarPoster, slug: "piacere-and-similar-verbs" },
  { id: "preterito-imperfecto", title: "Passato Prossimo vs Imperfetto", subtitle: "Photo vs film — the aspect choice", build: preteritoImperfectoPoster, slug: "passato-prossimo-vs-imperfetto" },
  { id: "pronoun-order", title: "Object-Pronoun Order (RID)", subtitle: "Stacking mi/ti/gli + lo/la", build: pronounOrderPoster, slug: "object-pronouns" },
  { id: "weirdo", title: "Subjunctive Triggers (WEIRDO)", subtitle: "What flips into the subjunctive", build: weirdoPoster, slug: "congiuntivo-triggers" },
];

export function referenceView(app, params = {}) {
  app.innerHTML = `
    <section>
      <h2 class="text-2xl font-bold text-sage-800 mb-1">Reference Shelf</h2>
      <p class="text-sage-600 text-sm mb-6 max-w-2xl">Every key distinction as a one-glance poster. Pair the picture with the rule — that's how it sticks. Tap any poster to read the full guide.</p>
      <div class="grid lg:grid-cols-2 gap-5" id="poster-wall"></div>
    </section>`;

  const wall = app.querySelector("#poster-wall");
  POSTERS.forEach((p) => {
    const card = document.createElement("div");
    card.id = `poster-${p.id}`;
    card.className = "scroll-mt-24";
    card.innerHTML = `
      <div class="flex items-baseline justify-between mb-2">
        <h3 class="font-bold text-lg text-sage-800">${p.title}</h3>
        <button data-slug="${p.slug}" class="poster-link text-xs font-medium text-sage-500 hover:text-sage-800">Read the full guide →</button>
      </div>
      <p class="text-xs text-sage-500 mb-2">${p.subtitle}</p>
      ${p.build()}
    `;
    wall.appendChild(card);
  });

  app.querySelectorAll(".poster-link").forEach((btn) =>
    btn.addEventListener("click", () => navigate("article", { slug: btn.dataset.slug }))
  );

  // Deep-link support: #/reference?poster=gustar scrolls that poster into view.
  if (params.poster && app.querySelector(`#poster-${params.poster}`)) {
    app.querySelector(`#poster-${params.poster}`).scrollIntoView({ behavior: "smooth", block: "start" });
  } else {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}
